import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { ContentGroup } from '@rossum/components/content-group/ContentGroup';
import { Alert, Button, Dialog, Link, Stack } from '@rossum/ui/material';
import { contactSupportLink } from '../../../constants/values';
import { boldText } from '../../../lib/formaterValues';
import { getRoleName } from '../../../redux/modules/groups/helpers';
import { userSelector } from '../../../redux/modules/user/selectors';
import { State } from '../../../types/state';
import { DeleteUserDialog } from '../../users/components/delete-user/DeleteUserDialog';

export const DeleteAccountSection = () => {
  const [open, setOpen] = useState(false);
  const intl = useIntl();
  const user = useSelector(userSelector);
  const groups = useSelector((state: State) => state.groups);
  const role = useMemo(
    () => getRoleName(user.groups, groups),
    [user.groups, groups]
  );

  const isOrgGroupAdmin = role === 'organization_group_admin';

  return (
    <ContentGroup
      title={intl.formatMessage({
        id: 'features.personalSettings.deleteAccount',
      })}
      description={intl.formatMessage({
        id: 'features.personalSettings.deleteAccount.description',
      })}
    >
      {isOrgGroupAdmin ? (
        <Alert severity="warning">
          <Stack alignItems="flex-start" spacing={2}>
            <span>
              {intl.formatMessage(
                {
                  id: 'features.personalSettings.deleteAccount.adminWarning',
                },
                { boldText }
              )}
            </span>
            <Button
              component={Link}
              href={contactSupportLink}
              color="secondary"
              variant="outlined"
            >
              {intl.formatMessage({
                id: 'features.personalSettings.deleteAccount.contactSupport',
              })}
            </Button>
          </Stack>
        </Alert>
      ) : (
        <>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="outlined"
              data-cy="delete-account-button"
              color="secondary"
              onClick={() => setOpen(true)}
            >
              {intl.formatMessage({
                id: 'features.personalSettings.deleteAccount.button',
              })}
            </Button>
          </Stack>
          <Dialog
            open={open}
            PaperProps={{
              sx: { width: 480, minHeight: 213, position: 'fixed' },
              elevation: 2,
            }}
            onClose={() => setOpen(false)}
            sx={{ transition: 'smooth' }}
          >
            {open ? (
              <DeleteUserDialog
                user={user}
                role={role}
                onCancel={() => setOpen(false)}
              />
            ) : null}
          </Dialog>
        </>
      )}
    </ContentGroup>
  );
};
